// StudioMobileFab — floating "Quick Action" FAB for mobile /studio.
// Sits bottom-right above the floating MobileBottomNav, expands on tap
// to reveal 3 one-tap shortcuts (Go Live, Record Story, Draft News Post).
// Hidden on lg+ desktop where the existing top-bar CTAs are reachable.
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Plus, Radio, Video, Newspaper, X } from "lucide-react";

export default function StudioMobileFab() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  // Auto-close on outside click. We use mousedown not click so React's
  // own click handlers (the toggle button + action items inside) win the
  // race against the global listener. Listener mounts on the next tick
  // so the same tap that opens the menu doesn't immediately close it.
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (!e.target.closest("[data-testid='studio-mobile-fab']")) setOpen(false);
    };
    const id = window.setTimeout(() => document.addEventListener("mousedown", close), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", close);
    };
  }, [open]);

  const isAdmin = user?.role === "admin";
  const canLive = user?.role === "premium_creator" || isAdmin;

  const ACTIONS = [
    canLive && { id: "live", label: "Go Live", icon: Radio, tint: "bg-tv-live", to: "/studio/live" },
    { id: "record", label: "Record Story", icon: Video, tint: "bg-fuchsia-500", to: "/studio?tab=upload" },
    isAdmin && { id: "news", label: "Draft News Post", icon: Newspaper, tint: "bg-tv-accent text-black", to: "/admin?tab=news" },
  ].filter(Boolean);

  return (
    <div
      data-testid="studio-mobile-fab"
      className="lg:hidden fixed bottom-24 right-4 z-40 flex flex-col items-end gap-2"
    >
      {open && ACTIONS.map((a, i) => {
        const Icon = a.icon;
        return (
          <button
            key={a.id}
            data-testid={`studio-fab-${a.id}`}
            onClick={() => { nav(a.to); setOpen(false); }}
            style={{ animationDelay: `${i * 40}ms` }}
            className={`group flex items-center gap-2.5 px-3.5 py-2.5 rounded-full text-white text-xs font-black uppercase tracking-wider shadow-lg ${a.tint} animate-in fade-in slide-in-from-bottom-2 duration-200`}
          >
            <Icon className="w-4 h-4" /> {a.label}
          </button>
        );
      })}
      <button
        type="button"
        data-testid="studio-fab-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close quick actions" : "Open quick actions"}
        aria-expanded={open}
        className={`w-14 h-14 rounded-full text-white shadow-[0_8px_28px_rgba(220,38,38,0.5)] flex items-center justify-center transition-transform ${open ? "bg-white/15 rotate-45 ring-1 ring-white/20" : "bg-tv-live hover:bg-red-600"}`}
      >
        {open ? <X className="w-5 h-5" /> : <Plus className="w-6 h-6" />}
      </button>
    </div>
  );
}
